import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useApp } from "@/state/AppContext";

const ANON_LIMIT = 2;
const ANON_TRIAL_DAYS = 7;

/** Track document usage. Logged-in → server counts; anonymous → localStorage. */
export default function useUsage() {
  const { token, authHeaders } = useApp();
  const [usage, setUsage] = useState({
    tier: "free", limit: ANON_LIMIT, used: 0, remaining: ANON_LIMIT,
    trial_expires_at: null, trial_expired: false, trial_days_total: ANON_TRIAL_DAYS,
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!token) {
      const key = "ba_anon_usage_v2";
      let stored = {};
      try { stored = JSON.parse(localStorage.getItem(key) || "null") || {}; } catch (_) { void 0; }
      const now = new Date();
      if (!stored.started_at) {
        stored = { started_at: now.toISOString(), count: 0 };
        try { localStorage.setItem(key, JSON.stringify(stored)); } catch (_) { void 0; }
      }
      const startedAt = new Date(stored.started_at);
      const expires = new Date(startedAt.getTime() + ANON_TRIAL_DAYS * 24 * 3600 * 1000);
      setUsage({
        tier: "free", limit: ANON_LIMIT, used: stored.count || 0,
        remaining: Math.max(0, ANON_LIMIT - (stored.count || 0)),
        trial_expires_at: expires.toISOString(),
        trial_expired: now >= expires,
        trial_days_total: ANON_TRIAL_DAYS,
        loading: false,
      });
      return;
    }
    try {
      const r = await axios.get(`${API}/usage`, { headers: authHeaders });
      setUsage({ ...r.data, loading: false });
    } catch {
      setUsage(u => ({ ...u, loading: false }));
    }
  }, [token, authHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  const bumpAnon = () => {
    if (token) return;
    const key = "ba_anon_usage_v2";
    let stored = { started_at: new Date().toISOString(), count: 0 };
    try { stored = JSON.parse(localStorage.getItem(key) || "null") || stored; } catch (_) { void 0; }
    stored.count = (stored.count || 0) + 1;
    localStorage.setItem(key, JSON.stringify(stored));
    refresh();
  };

  return { ...usage, refresh, bumpAnon };
}
